import React from 'react';
import { Divider } from '@mui/material';
import {
  numberToCurrencyString,
  numberToPercentageString,
  CalendarDay,
} from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { AllocationRule } from 'further-types/lp/allocations';
import GeneralCapitalCallAllocationsForm from './GeneralCapitalCallAllocationsForm';
import TabsPanel from 'further-ui/components/TabsPanel';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import AvatarWithName from 'components/new/AvatarWithName';

interface GeneralCapitalCallCardProps {
  actionIndex: number;
  generalCapitalCall: {
    actionId: string;
    name: string;
    ledgerDate: CalendarDay;
    actionDueDate?: CalendarDay;
    totalAmountLcy: number;
    allocationRule: AllocationRule;
    originalInvestorBreakdown: Array<{
      commitmentId: string;
      investorName: string;
      totalCommitmentLcy: number;
      calledFundsLcy: number;
      allocationPercentage: number;
    }>;
    newInvestorBreakdown: Array<{
      commitmentId: string;
      investorName: string;
      totalCommitmentLcy: number;
      calledFundsLcy: number;
      allocationPercentage: number;
      excludeFromAllocation: boolean;
      callOverpaidOrUnderpaid: number;
    }>;
    numberOfEntries: number;
  };
  baseCurrency: CurrencyCode;
}

const GeneralCapitalCallCard: React.FC<GeneralCapitalCallCardProps> = ({
  generalCapitalCall,
  baseCurrency,
  actionIndex,
}) => {
  const originalAllocationRows =
    generalCapitalCall.originalInvestorBreakdown.map((investor) => ({
      ...investor,
      id: investor.commitmentId,
    }));

  const originalAllocationColumns: Array<
    TableColumn<(typeof originalAllocationRows)[number]>
  > = [
    {
      key: 'investorName',
      label: 'Investor',
      render: ({ investorName }) => <AvatarWithName name={investorName} />,
    },
    {
      key: 'totalCommitmentLcy',
      label: `Total commitment (${baseCurrency})`,
      render: (row) =>
        numberToCurrencyString(row.totalCommitmentLcy, {
          currency: baseCurrency,
        }),
    },
    {
      key: 'calledFundsLcy',
      label: `Called funds (${baseCurrency})`,
      render: (row) =>
        numberToCurrencyString(row.calledFundsLcy, { currency: baseCurrency }),
    },
    {
      key: 'allocationPercentage',
      label: 'Allocation % of called funds',
      render: (row) =>
        numberToPercentageString(row.allocationPercentage, {
          accuracy: 2,
          noMultiply: true,
        }),
    },
  ];

  return (
    <>
      <Divider />
      <TabsPanel
        disableFullWidth
        tabs={[
          {
            label: 'New allocation',
            key: 'newAllocation',
            content: (
              <GeneralCapitalCallAllocationsForm
                fields={generalCapitalCall.newInvestorBreakdown}
                totalAmountLcy={generalCapitalCall.totalAmountLcy}
                allocationRule={generalCapitalCall.allocationRule}
                actionIndex={actionIndex}
                baseCurrency={baseCurrency}
                originalInvestorBreakdown={
                  generalCapitalCall.originalInvestorBreakdown
                }
              />
            ),
          },
          {
            label: 'Original allocation',
            key: 'originalAllocation',
            content: (
              <Table
                borderless
                rows={originalAllocationRows}
                columns={originalAllocationColumns}
              />
            ),
          },
        ]}
      />
    </>
  );
};

export default GeneralCapitalCallCard;
